import * as React from "react";
import "./sub-category-list-route.scss";

import {match} from "react-router";

import {Helmet}                                               from "@Common/libs/helmet/helmet";
import {ComponentLink, Link, makeChain, valueHelper as value} from "@Common/react/cst2";
import {UseState}                                             from "@Common/react/use-state";
import {Types}                                                from "@Common/types";
import {flatten1, sort}                                       from "@Common/utils/collections";
import {chain}                                                from "@Common/utils/fs";
import {oMapToArr}                                            from "@Common/utils/objects";
import {isBlankStr}                                           from "@Common/utils/strings";

import {consumeContext} from "@Xpresso/loaders/consume-context";

import {Breadcrumbs}   from "../common/breadcrumbs/breadcrumbs";
import {rProductTiles} from "../common/tiles/product-tiles";
import {Layout}        from "../layout/layout";

import {rTemplateViewMore} from "./template-view-more";

type RouteMatch = match<{cat_id: `${number}`}>;

export const SubCategoryListRoute = ({match}:{match: RouteMatch}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))
    .then(...consumeContext("company"))
    .then(...consumeContext("catSubStyle"))
    .then(...consumeContext("themeScheme"))
    .then(...consumeContext("routerHistory"))
    .then(...consumeContext("userLevels"))
    .then("listSelected", ({}, next:ComponentLink<typeof UseState<boolean>>) => UseState({
        initValue: !!sessionStorage.getItem("listingId"),
        next
    }))
    .then("mailingOnly", ({}, next:ComponentLink<typeof UseState<boolean>>) => UseState({
        initValue: !!sessionStorage.getItem("mailingOnly"),
        next
    }))
    .then("catSubStyle",  value(({catSubStyle}) => catSubStyle?.value))
    .then("cat", ({catSubStyle, routerHistory}, next:ComponentLink<typeof MatchCat>) => (
        // Need JSX for useEffect hook
        <MatchCat {...{catSubStyle, match, next, routerHistory}}/>
    ))
    .then(({cat, company, listSelected, mailingOnly, catSubStyle, apis, themeScheme, userLevels}) => {
        if (cat && Array.isArray(cat.child)) {
            if (userLevels?.listType && userLevels?.listType !== 6) {
                cat.child = cat.child.filter(child => child.hasNonNdmTemplate);
            }
        }
        return (<>
            <Helmet title={
                themeScheme.companyName === 'Xpressdocs' ? (
                    "Xpressdocs"
                ) : (
                    `${themeScheme.companyName} | ${cat && cat.name} | Xpressdocs`
                )
            }>
                <meta name="keywords" content={`${
                    themeScheme.companyName.split(' ').join(',')
                },${
                    cat && cat.name.split(' ').join(',')
                },Xpressdocs`.replace(/[^a-zA-Z0-9]+/g, ',')}/>
            </Helmet>
            <Layout className="sub-category-list-route-34k" active="catSubcatStyle">

                <div className="restrict-width-1">
                    <Breadcrumbs
                        cat={{name: cat && cat.name, id: match.params.cat_id}}
                        listSelected={listSelected}
                        mailingOnly={mailingOnly}
                    />

                    {!catSubStyle ? (
                        <div className="" xd-qa-type="label" xd-qa-name="Loading">
                            Loading...
                        </div>
                    )  : !cat ? (
                        <div className="" xd-qa-type="label" xd-qa-name="Empty">
                            No templates are available in this category!
                        </div>
                    ) : (
                        rProductTiles({
                            products:
                                chain(
                                    cat.child,
                                    // @ts-ignore
                                    (_) => sort(_, (sub: any) => sub.seq),
                                    (_) => sessionStorage.getItem("listingId") ? _.filter((sub) => sub.listingsEligible) : _,
                                    (_) => userLevels?.listSelected ? _.filter((template) => template.addressable) : _,
                                    (_) => mailingOnly?.value ? _.filter((template) => template.addressable && template.mailable) : _,
                                    (_) => userLevels?.listType == 6 ? _.filter((template) => template.isNdm) : _,
                                ),
                            getProductViewData: (sub) => ({
                                img_url: sub.image,
                                name: sub.name,
                                type: cat.categoryType,
                                href: isBlankStr(sub.link) ? null : sub.link,
                                to: `/cat/${match.params.cat_id}/sub/${sub.id}`,
                                new_tab: sub.newTab,
                                getIcon: () => company?.prefs.smartTemplateIcon && sub.hasCCTemplate && (
                                    <div className="icon" xd-qa-type="icon" xd-qa-name="Smart Template Icon">
                                        <img alt="Smart Template" src="/next_images/icons/SmartTemplateIcon.svg"/>
                                    </div>
                                ),
                                children: sort(sub.child, (style: any) => style.seq).map((style) => ({
                                    name: style.name,
                                    to: `/cat/${match.params.cat_id}/sub/${sub.id}?filter=${style.id}`,
                                })),
                            }),
                            view_more: {
                                getItems: async (sub) => {
                                    const styleTemplates = await apis.next.getStyleTemplate([sub.id]);
                                    return chain(
                                        flatten1(oMapToArr(styleTemplates, s=>s.templates)),
                                        (_) => sort(_, (template: any) => template.seq),
                                    );
                                },
                                renderItem: (template:any, {viewMode}) => rTemplateViewMore(template, {viewMode}),
                                alwaysShow: true,
                            },
                        })
                    )}
                </div>
            </Layout>
        </>);
    })
    .run()
);

export const MatchCat = ({catSubStyle, routerHistory, next, match}:{
    catSubStyle: Types.Xpresso.Loaders.ContextNameMap['catSubStyle']['value'],
    routerHistory: Types.Xpresso.Loaders.ContextNameMap['routerHistory'],
    next: Link<JSX.Element, Types.Apis.Payloads.Next.Catalog.GetCatSubStyleMy.Cat>,
    match: RouteMatch,
}) =>
{
    const cat = (
        catSubStyle?.[match.params.cat_id]
        || Object.values(catSubStyle ?? {}).find(cat => cat.categoryType === `system|${match.params.cat_id}`)
    );
    React.useEffect(() =>
    {
        const linkedCatId = /^system\|(\d+)$/.exec(cat?.categoryType)?.at(1);
        if (linkedCatId && linkedCatId !== match.params.cat_id) {
            routerHistory.replace(routerHistory.location.pathname.replace(match.params.cat_id, linkedCatId));
        }
    }, [cat]);
    return next(cat);
};

